import type { TefComs4csMicroFrontend } from '@mfe-4cs/components/tef-coms4cs';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { ConsentStore } from '@mfe-4cs/state/ConsentStore';
import comsStore from '@mfe-4cs/state/ComsStore';
import { localDate, localTime, replaceElements } from '@mfe-4cs/components/templates/util';

function computeClassName(consent: ConsentStore): string | undefined {
  if (consent.modifiedFromInitialSelfOrOneOfTheFeatures) {
    return 'edited';
  }
  if (consent.deprecated) {
    return 'outdated';
  }
  if (consent.granted && consent.hasNotGrantedFeature) {
    return 'part_agreed';
  }
  if (consent.granted) {
    return 'agreed';
  }
  if (consent.granted === undefined) {
    return undefined;
  }
  if (!consent.granted) {
    return 'rejected';
  }
  return undefined;
}

export function consentTemplate(this: TefComs4csMicroFrontend, consent: ConsentStore): TemplateResult {
  const featureTemplate = this.getTemplateFn('featureTemplate');

  const className = computeClassName(consent);

  return html`
    ${consent.hintBefore ? html`
      <div class="coms-hint-before">${unsafeHTML(consent.hintBefore)}</div>` : nothing}
    <div class="coms-panel ${className ? `coms-panel--${className}` : ''}">
      ${consent.modeconstraint === 'edit' ? html`<small class="join-hint">Diese Einwilligung ist aus der Zusammenführung der vorhergehenden Einwilligungen entstanden</small>` : nothing}
      <tef-coms-multi-checkbox
        value="${ifDefined(consent.granted)}"
        @change=${(event: { detail: boolean; }) => { consent.change(event.detail); }}
        ?disabled="${(comsStore.identity?.readonly || consent.deprecated) ?? false}"
      >
        <p>
          ${unsafeHTML(replaceElements.bind(this)(consent.scope))}
          ${comsStore.config?.showVersions ? html`<small>(Vers. ${consent.version})</small>` : nothing}
        </p>
        ${consent.features.map((f) => html`${featureTemplate(f, !!consent.deprecated)}`)}
        ${consent.client && consent.date ? html`
          <small>
            <tef-coms-icon icon="clock"></tef-coms-icon>
            <a
              role="button"
              @click=${() => { /* eslint-disable @typescript-eslint/indent */
                comsStore.history!.configId = consent.configId;
                comsStore.history!.consentShortScope = consent.shortScope;
              } /* eslint-enable @typescript-eslint/indent */}
            >
              Letzte Änderung am
              ${localDate(consent.date)} um ${localTime(consent.date)} Uhr über ${consent.clientDisplay ?? consent.client}
            </a>&nbsp;
          </small>
        ` : nothing}
      </tef-coms-multi-checkbox>
    </div>
    ${consent.hintAfter ? html`
      <div class="coms-hint-after">${unsafeHTML(consent.hintAfter)}</div>` : nothing}
  `;
}
